import { forwardRef } from 'react';
import { cn } from '../lib/utils.js';

// Minimal shadcn/ui-style primitives (variants without extra dependencies).

export const buttonVariants = {
  primary: 'bg-neutral-900 text-white shadow-sm hover:bg-neutral-700',
  secondary: 'border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50',
  ghost: 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900',
};

export const buttonSizes = {
  sm: 'h-8 gap-1.5 px-2.5 text-xs',
  md: 'h-9 gap-2 px-4 text-sm',
  icon: 'h-8 w-8',
};

export function buttonClass(variant = 'primary', size = 'md', className) {
  return cn(
    'inline-flex select-none items-center justify-center whitespace-nowrap rounded-md font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900/20 disabled:pointer-events-none disabled:opacity-50',
    buttonVariants[variant],
    buttonSizes[size],
    className
  );
}

export function Button({ variant = 'primary', size = 'md', type = 'button', className, ...props }) {
  return <button type={type} className={buttonClass(variant, size, className)} {...props} />;
}

export const Input = forwardRef(function Input({ className, ...props }, ref) {
  return (
    <input
      ref={ref}
      className={cn(
        'h-9 w-full rounded-md border border-neutral-300 bg-white px-3 text-sm text-neutral-900 placeholder:text-neutral-400 transition focus:border-neutral-400 focus:outline-none focus:ring-2 focus:ring-neutral-900/10 disabled:cursor-not-allowed disabled:bg-neutral-50',
        className
      )}
      {...props}
    />
  );
});

export const Textarea = forwardRef(function Textarea({ className, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      className={cn(
        'min-h-[76px] w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-400 transition focus:border-neutral-400 focus:outline-none focus:ring-2 focus:ring-neutral-900/10',
        className
      )}
      {...props}
    />
  );
});

export function Select({ className, ...props }) {
  return (
    <select
      className={cn(
        'h-9 rounded-md border border-neutral-300 bg-white px-2.5 text-sm text-neutral-900 transition focus:border-neutral-400 focus:outline-none focus:ring-2 focus:ring-neutral-900/10',
        className
      )}
      {...props}
    />
  );
}

export function Card({ className, ...props }) {
  return <div className={cn('rounded-lg border border-neutral-200 bg-white', className)} {...props} />;
}

export function Badge({ className, ...props }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-xs font-medium text-neutral-600',
        className
      )}
      {...props}
    />
  );
}

export function Label({ className, ...props }) {
  return <label className={cn('text-sm font-medium text-neutral-700', className)} {...props} />;
}
